import React, { useState } from "react";
import { Link, Outlet, useMatch, useLocation, matchPath } from "react-router-dom";
import { Button, Layout, Tooltip, Tour } from "antd";
import { useSize } from "./hooks/size";
import { useResizeDetector } from "react-resize-detector";
import logo from "./assets/wallonia.png";
import { useQueryParam, StringParam, BooleanParam } from "use-query-params";
import { isMobileMode } from "./components/Indicateurs/SidePanel/SidePanel";
import { useSidePanelStore } from "./hooks/sidePanel";
import { QuestionOutlined } from "@ant-design/icons";
import { useTour } from "./hooks/TourContext";
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { Bars3Icon, BellIcon, XMarkIcon } from "@heroicons/react/24/outline";

const { Content } = Layout;

export const useNoLayout = (): boolean => {
  const [nolayout = false] = useQueryParam("nolayout", BooleanParam);

  const foldMenu = useSidePanelStore((s) => s.setFolded);
  if (nolayout) foldMenu(true);

  return !!nolayout;
};

const menuItems = [
  {
    label: "Accueil",
    key: "",
    icon: require("@ant-design/icons").HomeOutlined,
  },
  {
    label: "Projections",
    key: "projections",
    icon: require("@ant-design/icons").LineChartOutlined,
  },
  {
    label: "Indicateurs",
    key: "indicateurs",
    icon: require("@ant-design/icons").CompassOutlined,
  },
  {
    label: "Coût Inaction",
    key: "cout-inaction",
    icon: require("@ant-design/icons").EuroOutlined,
  },
];

const AppLayout = () => {
  const noLayout = useNoLayout();
  const { width = 0, ref } = useResizeDetector();
  const ratio = width < 500 ? width / 500 : 1;
  const location = useLocation();
  //@ts-ignore
  const [tour, setTour, tourSteps] = useTour();

  if (noLayout)
    return (
      <Content
        style={{
          height: `${100}vh`,
          position: "relative",
        }}
      >
        <Outlet />
      </Content>
    );
  return (
    <div
      ref={ref}
      style={{
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
      }}
    >
      <Disclosure as="nav" className="bg-white shadow-md" style={{ boxShadow: "inset 0 -1px #D3D3D3", zIndex: 5000 }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 justify-between">
            <div className="flex">
              <div className="flex shrink-0 items-center">
                <img src={logo} alt="spw logo" className="h-8 w-auto mr-8" />
                <span className="mr-8 text-xl/7 font-semibold text-red-600">Portail Climat</span>
                <span style={{ color: "grey" }}>{width < 700 ? "AwAC" : "Agence wallonne de l'air et du climat"}</span>
              </div>
            </div>
            <div className="hidden lg:ml-6 lg:flex lg:space-x-8">
              {menuItems.map((item) => {
                // @ts-ignore
                //const match = useMatch(item.key);
                return (
                  <div
                    key={item.key}
                    className={
                      matchPath(item.key, location.pathname)
                        ? "inline-flex items-center border-b-2 border-red-500 px-1 pt-1 text-sm font-medium text-gray-900"
                        : "inline-flex items-center border-b-2 border-transparent px-1 pt-1 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    }
                  >
                    <Link key={item.key} to={`/${item.key}`} className="no-color">
                      <item.icon className="mr-2 h-5 w-5" />
                      <span>{item.label}</span>
                    </Link>
                  </div>
                );
              })}
            </div>
            <div className="hidden lg:ml-6 lg:flex lg:items-center">
              <Tooltip title="Tour (Aide)" placement="rightBottom">
                <Button
                  shape="circle"
                  icon={<QuestionOutlined />}
                  onClick={() => {
                    // @ts-ignore
                    setTour({ id: location.pathname.split("/")[1] });
                  }}
                  disabled={["", "cout-inaction"].includes(location.pathname.split("/")[1])}
                />
              </Tooltip>
              <Tour
                // @ts-ignore
                open={tourSteps && tourSteps.length > 0 ? true : false}
                onClose={() => {
                  // @ts-ignore
                  setTour(null);
                }}
                steps={tourSteps}
                disabledInteraction={false}
                onChange={(step) => {
                  // @ts-ignore
                  setTour({ id: tour.id, step });
                }}
              />
            </div>
            <div className="-mr-2 flex items-center lg:hidden">
              {/* Mobile menu button */}
              <DisclosureButton className="group relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500">
                <span className="absolute -inset-0.5" />
                <span className="sr-only">Open main menu</span>
                <Bars3Icon aria-hidden="true" className="block size-6 group-data-[open]:hidden" />
                <XMarkIcon aria-hidden="true" className="hidden size-6 group-data-[open]:block" />
              </DisclosureButton>
            </div>
          </div>
        </div>

        <DisclosurePanel className="lg:hidden">
          <div className="space-y-1 pb-3 pt-2">
            {menuItems.map((item) => {
              // @ts-ignore
              //const match = useMatch(item.key);
              return (
                <Link key={item.key} to={`/${item.key}`}>
                  <DisclosureButton
                    as="a"
                    key={item.key}
                    className={
                      matchPath(item.key, location.pathname)
                        ? "block border-l-4 border-red-500 bg-red-50 py-2 pl-3 pr-4 text-base font-medium text-red-700"
                        : "block border-l-4 border-transparent py-2 pl-3 pr-4 text-base font-medium text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700"
                    }
                  >
                    <span>{item.label}</span>
                  </DisclosureButton>
                </Link>
              );
            })}
          </div>
        </DisclosurePanel>
      </Disclosure>
      <Layout
        style={{
          height: `calc(${(1 / ratio) * 100}vh - ${64 * (1 / ratio)}px)`,
          minWidth: width && 500,
          transformOrigin: "top left",
          transform: `scale(${ratio})`,
        }}
      >
        <Layout>
          <Content
            style={{
              height: `calc(${(1 / ratio) * 100}vh - ${64 * (1 / ratio)}px)`,
              overflowY: "scroll",
              position: "relative",
            }}
          >
            <Outlet />
          </Content>
        </Layout>
      </Layout>
    </div>
  );
};

export default AppLayout;
